import { FTLShipment, LTLShipment } from "@src/types/api";
import {
  getFTLOriginDestinationFromFields,
  getLTLOriginDestinationFromFields,
  toLocationDisplay,
  type LocationDisplay,
  type OriginDestination,
} from "./formUtils";

export const formatNumber = (value: number) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
export const formatCurrency = (value: number) => `$${formatNumber(value)}`;

const EMPTY_LOCATION: LocationDisplay = { line1: "—", line2: "" };

function locationDisplaysFromOriginDestination(od: OriginDestination): {
  origin: LocationDisplay;
  destination: LocationDisplay;
} {
  return {
    origin: toLocationDisplay(od.originCityState, od.originZip),
    destination: toLocationDisplay(od.destinationCityState, od.destinationZip),
  };
}

export function getFTLOriginDestination(shipment: FTLShipment): {
  origin: LocationDisplay;
  destination: LocationDisplay;
} {
  const rf = shipment.requiredFields;

  if (!rf) return { origin: EMPTY_LOCATION, destination: EMPTY_LOCATION };
  return locationDisplaysFromOriginDestination(getFTLOriginDestinationFromFields(rf));
}
export function getLTLOriginDestination(shipment: LTLShipment): {
  origin: LocationDisplay;
  destination: LocationDisplay;
} {
  const rf = shipment.requiredFields;

  if (!rf) return { origin: EMPTY_LOCATION, destination: EMPTY_LOCATION };
  return locationDisplaysFromOriginDestination(getLTLOriginDestinationFromFields(rf));
}
export function getLTLWeight(shipment: LTLShipment): number {
  const r = shipment.requiredFields;
  const fromTotal = r?.totalWeight?.value ? parseFloat(String(r.totalWeight.value)) : 0;
  const fromItems =
    Array.isArray(r?.items) && r.items.length > 0
      ? r.items.reduce((sum, i) => sum + (Number(i?.weight) || 0), 0)
      : 0;

  return fromTotal || fromItems || 0;
}
export function getFTLWeight(shipment: FTLShipment): number {
  const rf = shipment.requiredFields;

  if (!rf) return 0;
  const tw = (
    rf as {
      totalWeight?: {
        value?: unknown;
      } | null;
    }
  ).totalWeight;

  if (tw && typeof tw === "object" && "value" in tw) {
    const v = tw.value;
    const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));

    if (Number.isFinite(n) && n > 0) return n;
  }
  const weightRaw: unknown = rf.weight;

  if (typeof weightRaw === "number" && Number.isFinite(weightRaw) && weightRaw > 0) {
    return weightRaw;
  }
  if (typeof weightRaw === "string" && weightRaw.trim()) {
    const n = parseFloat(weightRaw.replace(/,/g, ""));

    if (Number.isFinite(n) && n > 0) return n;
  }
  if (Array.isArray(rf.items) && rf.items.length > 0) {
    const sum = rf.items.reduce((acc, i) => acc + (Number(i?.weight) || 0), 0);

    if (sum > 0) return sum;
  }
  const nd = shipment.normalizedDraft;

  if (
    nd?.form &&
    typeof nd.form.weight === "number" &&
    Number.isFinite(nd.form.weight) &&
    nd.form.weight > 0
  ) {
    return nd.form.weight;
  }
  return 0;
}
